/* eslint-disable no-restricted-syntax */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import ReactSVG from 'react-svg';
import upload from 'cloud-upload.svg';
import axios from 'axios';
import AdditionalImage from './AdditionalImage';
import useDebounce from '../hooks/useDebounce';

const MAX_IMAGE_LENGTH = 12;

export default function AdditionalImageUpload({ listingId, listingImages }) {
  const fileInput = useRef(null);

  const [files, setFiles] = useState([]);
  const [shouldUploadFiles, setShouldUploadFiles] = useState(false);

  // Prefill image files
  useEffect(() => {
    const filteredImages = listingImages.filter(image => image);

    setFiles([...filteredImages]);
  }, [listingImages]);

  const handleClick = () => {
    if (fileInput && fileInput.current) {
      fileInput.current.click();
    }
  };

  const orderImage = (index, direction) => {
    if (!direction) return null;
    const newIndex = direction === 'forwards' ? index - 1 : index + 1;

    const filesCopy = [...files];
    // Remove image from files array and store in variable
    const currentImage = filesCopy.splice(index, 1)[0];
    // Insert back image into files array in new index
    filesCopy.splice(newIndex, 0, currentImage);

    setFiles(filesCopy);

    setShouldUploadFiles(true);
  };

  const uploadPhotos = useCallback(async () => {
    const formData = new FormData();

    // Forgive my sins
    // jQuery was the only way I could pass the listing id when creating a listing
    // (yes I tried using a ref and plain javascript)
    const buttonListingId = $('#additional-images-submit').data().listingid;

    formData.append('id', listingId || buttonListingId);

    for (const [index, file] of files.entries()) {
      const existingImage = !!file.image_url;
      formData.append(`image_${index}`, existingImage ? file.signed_id : file);
    }

    const tokenElement = document.querySelector('[name=csrf-token]');
    const res = await axios.put('/api/v1/update_photos', formData, {
      headers: {
        'X-CSRF-Token': tokenElement ? tokenElement.content : '',
        Accept: 'application/json',
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!listingId) {
      window.location.replace('/account');
      return null;
    }

    if (res.status === 204) {
      const event = new CustomEvent('toast-message', {
        detail: 'Additional images successfully updated',
      });
      document.dispatchEvent(event);

      window.location.reload();
    }
  });

  const handleFileInput = e => {
    const newFiles = Array.from(e.target.files);
    const combinedFiles = [...files, ...newFiles];
    if (combinedFiles.length > MAX_IMAGE_LENGTH) {
      const overflowItemsLength =
        files.length + newFiles.length - MAX_IMAGE_LENGTH;
      const combineFilesRestricted = combinedFiles.slice(
        0,
        -overflowItemsLength
      );
      setFiles(combineFilesRestricted);
      const event = new CustomEvent('toast-message', {
        detail: `Oops! Looks like you hit your limit on photos for this business listing. We can only show ${MAX_IMAGE_LENGTH} additional images at a time.`,
      });
      document.dispatchEvent(event);
    } else {
      setFiles([...files, ...newFiles]);
    }
    setShouldUploadFiles(true);
  };

  useDebounce(
    () => {
      if (shouldUploadFiles) {
        uploadPhotos();
        setShouldUploadFiles(false);
      }
    },
    2500,
    [shouldUploadFiles]
  );

  return (
    <>
      <label>
        Additional Images (
        <span className="additional-image-count">{files.length}</span> of{' '}
        {MAX_IMAGE_LENGTH})
      </label>
      <div className="additional-images-container">
        {files.map((file, index) => {
          const existingImage = !!file.signed_id;

          const fileIdentifier = existingImage
            ? file
            : `${file.name}-${file.lastModified}`;

          const clearImage = () => {
            // Create a copy
            const filesCopy = Array.from(files);
            // Find the index of the image
            const fileIndex = filesCopy.findIndex(
              copyFile =>
                (existingImage
                  ? copyFile
                  : `${copyFile.name}-${copyFile.lastModified}`) ===
                fileIdentifier
            );
            // Remove the image from the files
            filesCopy.splice(fileIndex, 1);
            // Update state
            setFiles(filesCopy);
            setShouldUploadFiles(true);
          };

          return (
            <AdditionalImage
              key={index}
              index={index}
              src={existingImage ? file.image_url : URL.createObjectURL(file)}
              clearImage={clearImage}
              alt={existingImage ? file.image_url : file.name}
              firstImage={index === 0}
              lastImage={index === files.length - 1}
              orderImage={orderImage}
            />
          );
        })}
        <div className="upload-additional-image-outer">
          <button
            className="upload-additional-image"
            type="button"
            onClick={handleClick}
          >
            <ReactSVG src={upload} className="upload-svg" />
          </button>
        </div>
      </div>

      <input
        ref={fileInput}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileInput}
        accept="image/jpeg, image/png, image/gif"
      />

      <button
        type="button"
        id="additional-images-submit"
        data-listingid={listingId || null}
        className="hidden"
        onClick={uploadPhotos}
      >
        Button
      </button>
    </>
  );
}
